import { useMemo, useState, type ReactNode } from 'react'
import {
  RELATED_PRESENTATIONS, arrangeConnections, connectionTotal,
  type ConnectionArrangement, type ConnectionGroup as GovernedConnectionGroup, type ConnectionItem as GovernedConnectionItem,
  type RelatedPresentation,
} from '@solum/experience'
import { EntityIcon } from '../EntityIcon'
import { Clock, Eye, LayoutGrid, Map as MapIcon, Plus, Search, Table } from 'lucide-react'
import { PresentationSwitcher } from '../PresentationSwitcher'
import { Button } from '../controls/Button'
import { CountPill } from '../controls/CountPill'
import { IconTile } from '../controls/IconTile'
import { SearchField } from '../controls/SearchField'
import { matchesText } from '../textSearch'
import { cx } from '../controls/cx'
import { readableProblem } from '../controls/readable'
import s from './Connections.module.css'

/**
 * ONE Connections surface, in two presentations.
 *
 * Read `packages/experience/src/connections.ts` for the model and the defect it
 * closes. What this component adds is the two shapes the same groups have to
 * take, and the fact that it is the SAME component in both:
 *
 *   panel      the drawer — chips, compact rows, fast contextual work in ~380px
 *   workspace  the record page — the same groups as raised plates in one
 *              recessed well (#2406, leading design), each plate headed by its
 *              type, count, view and Add; no chip strip
 *
 * The chips are the drawer's alone. In a 380px drawer a chip filters to one
 * group; on the page they used to be anchors that scrolled to a section, which
 * repeated every plate's heading in a strip above them — the page narrows its
 * groups with the filter instead.
 *
 * It fetches nothing and knows no entity. Titles, reachability and the
 * arrangement all arrive resolved, because each of those is a governed question
 * (`core.titles_of` is `security invoker`; the arrangement comes from the
 * `core.related_order` cascade) and a presentational component must not be able
 * to answer one.
 *
 * ── Two searches, deliberately named apart ──────────────────────────────────
 * The box in the header filters WHAT IS ALREADY LOADED and says so. Finding a
 * record that is not on this page is `onAdd`'s job, and that goes through the
 * governed lookup, which searches only what the caller may reach. One of these
 * can leak and the other cannot, so they never share a control.
 */

/**
 * The related surface's finite vocabulary.
 *
 * Aliased to `@solum/experience`'s `RelatedPresentation` rather than restated
 * (#2276): this used to be a browser constant, which meant no deployment could
 * offer or withhold a lens and no administrator could name the default. It is
 * governed data now — `core.view_kind.surfaces` — and two copies of a
 * vocabulary is how the copy nobody edits goes stale.
 */
export type ConnectionsLens = RelatedPresentation
export type ConnectionsPresentation = 'panel' | 'workspace'

/** The words a person sees. The storage keys stay the governed vocabulary. */
const LENS_LABEL: Record<ConnectionsLens, string> = {
  grid: 'Grid', cards: 'Cards', map: 'Map', peek: 'Peek', timeline: 'Timeline',
}

/**
 * The GLYPHS, in the governed order (#2349 §D2/§D3).
 *
 * The design asks for *"a compact icon-based selector beside the type name"*
 * with the eligible views right-aligned. An icon alone is not a name, so every
 * option keeps `LENS_LABEL` as its accessible name and its tooltip — what the
 * compact form drops is pixels, never meaning, which is the same rule the
 * account trigger follows one shell out.
 *
 * Order is `RELATED_PRESENTATIONS`', not this map's: an object's key order is
 * not a contract, and the control renders the OFFERED array, which is already
 * filtered in the governed order by `bindingOf`.
 */
const LENS_GLYPH: Record<ConnectionsLens, typeof Table> = {
  grid: Table, cards: LayoutGrid, map: MapIcon, peek: Eye, timeline: Clock,
}

/** What one relationship group reads as, and what it is allowed to read as. */
export interface ConnectionsLensBinding {
  active: ConnectionsLens
  available: readonly ConnectionsLens[]
  /**
   * HOW `active` was chosen, where the host knows (#2349 §D3 — *"visibly
   * indicates inherited/default/refused state where applicable"*).
   *
   * The server already answers this per slot and the console already reads it;
   * until now it was computed and thrown away. Three states matter to a person:
   *
   *   · `default`   — nobody chose; this is what the deployment governs.
   *   · `requested` — this person chose it, and it stuck.
   *   · `refused`   — this person chose something this slot does not offer, so
   *                   the governed answer stands. A refusal that looks identical
   *                   to a success is the one state a selector must never hide.
   *
   * Absent ⇒ nothing is said. A host that cannot distinguish them must not
   * guess, because "your choice" and "the default" are different sentences.
   */
  source?: 'default' | 'requested' | 'refused'
  /**
   * WHICH rung decided the default, in the host's own vocabulary
   * (`source-slot`, `source`, `target`, `platform`). Rendered only as part of
   * the explanation of an inherited value; never as a control.
   */
  rung?: string
}

export interface ConnectionsProps {
  groups: readonly ConnectionGroup[]
  presentation?: ConnectionsPresentation
  arrangement?: ConnectionArrangement
  /**
   * How ONE group reads, and what it may read as — governed, per registered
   * relationship slot (#2276 owner correction).
   *
   * Per GROUP rather than per surface, and the distinction is the owner's:
   * "how do I read the people here" and "how do I read the addresses here" are
   * different questions. One lens for the whole surface meant a single address
   * group could drag an entire record page onto a canvas, and it meant a person
   * who wanted their people as cards got their addresses as cards too.
   *
   * Absent, or absent for one group, means the lenses every host can draw with
   * no declaration at all — deliberately not the WHOLE vocabulary: map needs a
   * recipe, and a control offering a lens with no canvas behind it is the blank
   * surface this component exists to refuse.
   */
  lensOf?: (groupKey: string) => ConnectionsLensBinding | undefined
  /** This person's choice for ONE group. Siblings are untouched. */
  onLensChange?: (groupKey: string, lens: ConnectionsLens) => void
  /**
   * The map canvas for ONE group, supplied by the host (#2276).
   *
   * A render slot for the same reason `renderGroupBody` is one: this component
   * knows no entity and resolves no coordinate, and the host is the only thing
   * that can turn a group's rows into pins. Absent ⇒ the map lens is not offered
   * however the deployment is configured, which is the host saying "I cannot
   * draw this" in the only way that cannot be wrong.
   *
   * It draws INSIDE the group's own section, beside the rows it could not
   * place — never above the page, where it would speak for relationships it
   * knows nothing about.
   */
  renderMap?: (group: ConnectionGroup) => ReactNode
  /**
   * Draw ONE group's timeline (#2323). Absent ⇒ Timeline is not offered here.
   *
   * The same seam as `renderMap` and for the same measured reason: the
   * resolver's floor offer is the whole registered vocabulary, so the moment
   * `timeline` is a `core.view_kind` row EVERY undeclared slot starts offering
   * it. Without this guard the selector would show a lens whose body is the
   * ordinary rows — a control that changes a label and nothing else.
   *
   * It draws INSIDE the group's own section, above that section's rows, which
   * stay: a chart is a reading of the records, not a replacement for them.
   */
  renderTimeline?: (group: ConnectionGroup) => ReactNode
  /** Open the far record. Absent ⇒ rows are text, not buttons. */
  onOpen?: (item: ConnectionItem) => void
  /** Start the governed add flow for one group. Absent ⇒ no Add is offered. */
  onAdd?: (group: ConnectionGroup) => void
  /**
   * Is `onAdd` TRUTHFULLY offered for this group? (#2849) Absent ⇒ every group
   * offers it. The host's, because what the server lets this caller write
   * differs per relationship and this component knows no write contract;
   * `false` withdraws BOTH of the group's Add paths — the heading's and the
   * known-empty row's "Add existing" — rather than offering a control whose
   * write the server will refuse.
   */
  addOffered?: (group: ConnectionGroup) => boolean
  /**
   * The words on one group's Add, on the page ("Add address"). The host's,
   * because the SINGULAR noun is governed (`core.entity_identity`) and a group
   * carries only its plural label. Absent ⇒ "Add" plus that plural label.
   */
  addLabelOf?: (group: ConnectionGroup) => string | undefined
  /**
   * Create a NEW far record for one group (#1992).
   *
   * Separate from `onAdd` because they are two different questions, and a
   * person standing in front of an empty relationship is asking one of them
   * on purpose: *is there already one of these* is a governed lookup, *make
   * me one* is a form. Offering only the combined control made the second
   * reachable solely by first pretending to do the first.
   */
  onCreate?: (group: ConnectionGroup) => void
  /**
   * Does the HOST know this group is empty? (#1992)
   *
   * Not derivable here. `group.count` cannot answer it: a count of 0 and a
   * count the server refused to derive arrive as the same number, and a host
   * rendering its own `renderGroupBody` may have rows this component never
   * sees.
   *
   * Only a host that KNOWS may say so, and only a known emptiness earns the
   * row — drawing “nothing here yet, make one” over rows RLS is merely hiding
   * is how a surface invites a duplicate of a record you cannot see.
   */
  isGroupEmpty?: (group: ConnectionGroup) => boolean
  /**
   * The heading over the whole surface. Defaults to the platform's own word.
   * A host passes one only where the PERSON named the section (the record
   * page's `core.related_order` sections) — this component does not know what
   * a section is and never invents a name for one.
   */
  heading?: ReactNode
  /**
   * Contextual controls owned by the host, rendered in this surface's header.
   *
   * The component stays data-free: it neither invents an action nor decides
   * who may use one. This slot exists so a host does not have to draw a
   * detached strip above Connections for a control that changes Connections.
   */
  actions?: ReactNode
  /**
   * Render one group's body with the host's own richer row.
   *
   * The record page's rows are the relationship GRID — declared edge facts as
   * columns, inline editing of those facts, a drill into the next band — and
   * duplicating any of that here would be the fourth implementation this
   * component exists to delete. So the host keeps its grid and this keeps the
   * arrangement, the counts, the anchors and the chrome.
   *
   * Still data-free: a `ConnectionGroup` goes out, nodes come back. This
   * component learns nothing about entities, routes or reads by offering it.
   * When it is supplied the in-page filter matches group LABELS only — the rows
   * belong to the host, and filtering on the handful this component happens to
   * hold would silently hide groups that do contain the match.
   */
  renderGroupBody?: (group: ConnectionGroup) => ReactNode
  /**
   * The host's own controls for ONE group, drawn in that group's header
   * between its count and its Add (#2406, leading design: the "All addresses ▾"
   * view chip). A render slot for the reason `renderGroupBody` is one — a saved
   * view is a governed read of the far entity, which this component cannot make.
   */
  renderGroupTools?: (group: ConnectionGroup) => ReactNode
  busy?: boolean
  /** A refusal or a failure, already worded by the host. */
  problem?: ReactNode
  /** Something is missing and the host knows it — a partial answer, said out loud. */
  partial?: ReactNode
  empty?: ReactNode
  testId?: string
}

export function Connections({
  groups, presentation = 'panel', arrangement, lensOf, onLensChange, renderMap, renderTimeline,
  onOpen, onAdd, addOffered, addLabelOf, onCreate, isGroupEmpty, heading = 'Connections', actions, renderGroupBody, renderGroupTools,
  busy, problem, partial,
  empty = 'Nothing is linked to this yet.', testId = 'connections',
}: ConnectionsProps) {
  const [query, setQuery] = useState('')
  const [only, setOnly] = useState<string | null>(null)
  // On a page a chip is an ANCHOR; in a drawer it is a filter. See the header.
  const anchored = presentation === 'workspace'

  const arranged = useMemo(() => arrangeConnections(groups, arrangement), [groups, arrangement])
  const chosen = useMemo(
    () => (only && !anchored ? arranged.filter((g) => g.key === only) : arranged),
    [arranged, only, anchored])
  const shown = useMemo(
    () => filterGroups(chosen, query, !!renderGroupBody), [chosen, query, renderGroupBody])
  const total = useMemo(() => connectionTotal(arranged), [arranged])
  const totalLabel = total.unknown && total.n === 0
    ? 'Connection count unavailable'
    : `${total.atLeast || total.unknown ? 'At least ' : ''}${total.n} connected`

  /* What ONE group offers, and what it is actually DRAWING.

     `drawn` is not the governed `active`: a stored or governed choice can name
     a lens this host cannot render — the map with no `renderMap`, most
     obviously — and the contract is that it says so by drawing the next one it
     has rather than a blank acre. Falling back here rather than in the host
     keeps each section's control and its own body agreeing about which lens is
     on, which they could not do while one surface-level value spoke for all of
     them. */
  const bindingOf = useMemo(() => (key: string) => {
    const governed = lensOf?.(key)
    // Told nothing, a surface offers what every host can draw without any
    // declaration. Map is not in it: it needs a recipe, and a control that
    // offers a lens with no canvas behind it is the blank surface this
    // component exists to refuse. Metro left this list in #2324 — it is not a
    // way of rendering ONE relationship list any more, and the whole-record
    // Graph workspace is where it went.
    const base = governed?.available ?? (['grid', 'cards'] as const)
    const offered = RELATED_PRESENTATIONS.filter(
      (k) => base.includes(k)
        && (k !== 'map' || !!renderMap)
        && (k !== 'timeline' || !!renderTimeline))
    const wanted = governed?.active ?? 'grid'
    const drawn: ConnectionsLens = offered.includes(wanted) ? wanted : (offered[0] ?? 'grid')
    /* A SUBSTITUTION IS A REFUSAL, and it must read as one (#2349 §D3).
       The server can answer `requested` for a lens THIS host cannot draw — a
       map with no `renderMap` behind it is the obvious case — and the fallback
       above then quietly draws something else. Reporting the server's
       `requested` there would tell a person their choice took effect while they
       are looking at a different view. So the host's own substitution wins, and
       it is spelled `refused` because that is what happened to the request. */
    const source = governed?.source != null && drawn !== wanted ? 'refused' : governed?.source
    return { offered, drawn, source, rung: governed?.rung }
  }, [lensOf, renderMap, renderTimeline])

  const pinned = new Set(arrangement?.pinned ?? [])
  const declaredKeys = new Set(arrangement?.declared ?? [])

  /* EVERY group, always, each drawing itself in its own lens. There is no
     branch here any more — which is the correction in one line: a surface
     cannot choose a presentation, because a surface is not the thing a
     presentation is chosen for. */
  const sections = shown.map((g) => {
    const { offered, drawn, source, rung } = bindingOf(g.key)
    return (
      <GroupSection key={g.key} group={g} presentation={presentation}
        pinned={pinned.has(g.key)} declared={declaredKeys.has(g.key)}
        onOpen={onOpen} onAdd={addOffered?.(g) === false ? undefined : onAdd} onCreate={onCreate}
        addLabel={addLabelOf?.(g)}
        knownEmpty={isGroupEmpty?.(g) ?? false} testId={testId}
        lens={drawn} offered={offered}
        onLensChange={onLensChange && presentation === 'workspace'
          ? (next) => onLensChange(g.key, next)
          : undefined}
        source={source} rung={rung}
        renderMap={renderMap} renderTimeline={renderTimeline}
        tools={renderGroupTools?.(g)}
        body={renderGroupBody?.(g)} />
    )
  })

  // No `data-lens` on the surface. There is no such thing any more: each section
  // carries its own, because each section decides its own.
  return (
    <section className={cx(s.root, presentation === 'workspace' && s.workspace)}
      data-testid={testId} data-presentation={presentation}>
      <header className={s.head}>
        {/* The page's second level: the record's name is the h1, each group
            below is an h3. In the drawer it is a caption, not an outline level. */}
        {anchored
          ? <h2 className={s.title}>{heading}</h2>
          : <span className={s.title}>{heading}</span>}
        <CountPill role="img" tone="outline" className={cx(s.count, 'tnum')}
          data-testid={`${testId}-count`} aria-label={totalLabel}>
          {/* `core.related_counts` caps and says so, so a capped total is a
              FLOOR. "at least 99" is a true sentence; "99" would not be.

              #2131: an UNCOUNTED group makes it a floor for a second, different
              reason — only server counts and visible rows contribute. Either
              fact alone earns the phrase, so they are OR'd rather than ranked;
              saying a bare total when either holds would not be true. When the
              floor is zero, "at least 0" communicates nothing, so the surface
              declines the count instead. */}
          {totalLabel}
        </CountPill>
        <span className={s.grow} />
        {actions}
        {/* The lens control used to live HERE, once, speaking for the whole
            record. It is in each section's own header now — that is the owner
            correction, and it is the difference between "how does this page
            read" and "how do I read the addresses on it". */}
      </header>

      {/* THE CHIPS ARE THE DRAWER'S (#2406, leading design). In a 380px panel a
          chip filters to one group, which is the fastest way through a short
          list. On the page they were anchors — and the page's groups are
          already one scroll apart, each named in its own plate, with the filter
          below narrowing them by name. The design draws no strip there, and a
          row of buttons that each repeat a heading on the same screen is the
          "same words three times" mock item 24 measured. */}
      {!anchored && arranged.length > 1 && (
        <div className={s.chips} role="group" aria-label="Relationship groups">
          <Chip label="All" count={total.unknown && total.n === 0 ? null : total.n}
            atLeast={total.atLeast || total.unknown}
            active={only === null} onClick={() => setOnly(null)}
            testId={`${testId}-chip-all`} />
          {arranged.map((g) => (
            <Chip key={g.key} label={g.label} count={g.count} atLeast={g.capped}
              pinned={pinned.has(g.key)}
              active={only === g.key}
              onClick={() => setOnly(only === g.key ? null : g.key)}
              testId={`${testId}-chip-${g.key}`} />
          ))}
        </div>
      )}

      {arranged.length > 0 && (
        /* The shared `SearchField`: the recess, the magnifier set into it and
           the room the text leaves for it are the kit's, not this surface's. */
        <SearchField icon={<Search size={14} />} wrapClassName={s.filter} className={s.search}
          type="search" value={query} data-testid={`${testId}-search`}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={renderGroupBody ? 'Filter relationships…' : 'Filter what is shown'}
          aria-label={renderGroupBody
            ? 'Filter the relationships on this page by name'
            : 'Filter the connections on this page'} />
      )}

      {problem && <p className={s.problem} data-testid={`${testId}-problem`}>{readableProblem(problem)}</p>}
      {busy && !arranged.length && <p className={s.muted} data-testid={`${testId}-busy`}>Looking…</p>}
      {!busy && !problem && !arranged.length && (
        <p className={s.muted} data-testid={`${testId}-empty`}>{empty}</p>
      )}
      {arranged.length > 0 && shown.length === 0 && (
        <p className={s.muted} data-testid={`${testId}-no-match`}>
          Nothing here matches “{query}”. Add searches everywhere you can reach.
        </p>
      )}

      {/* ON THE PAGE the groups sit in ONE recess, each a raised plate — the
          leading design's well. In the drawer they stay a plain list: a 380px
          panel has no room for a well and a plate inside it. */}
      {anchored && sections.length > 0
        ? <div className={s.well} data-testid={`${testId}-groups`}>{sections}</div>
        : sections}

      {partial && <p className={s.muted} data-testid={`${testId}-partial`}>{partial}</p>}
    </section>
  )
}

/**
 * The section's DOM id: a chip's anchor, and stable across a rerender.
 *
 * The colons come OUT. A group key is `works_at:out` (and `works_at:in:
 * core_person` on the record page, where the group is a slot), and while
 * `id="connections-section-works_at:out"` is legal HTML it is not a legal CSS
 * selector — `querySelector('#connections-section-works_at:out')` throws
 * `SyntaxError`, and so does `[data-testid^=…:…]` unquoted, which is how this
 * repo's browser specs address sections. An anchor nobody can select is not an
 * anchor. Two colons collapse to two separators, so the id stays reversible
 * enough to read and stable for as long as the key is.
 */
const sectionId = (testId: string, groupKey: string): string =>
  `${testId}-section-${groupKey.replace(/:/g, '--')}`

/**
 * A group nobody can name reads as counts, not as a list of anonymous rows.
 * Empty is the same case with the count coming from the server instead.
 */
function countsOnly(group: ConnectionGroup): boolean {
  return group.items.length === 0
    || group.items.every((i) => i.nameable === false || !i.title?.trim())
}

/**
 * The count face of a group: what it holds, per far entity.
 *
 * `capped` is stated per GROUP while the counts are per far entity, so a group
 * where any one entity hit `core.related_counts`' cap marks all of them "at
 * least". That over-states the UNCERTAINTY and never the number — "at least 3"
 * is true when the truth is exactly 3, while a bare "3" is false when the truth
 * is 300, and the header above has already called the whole total a floor.
 */
function countFace(group: ConnectionGroup): string {
  return group.byType
    .map((t) => t.count === null
      // #2131: "People: 0" over a slot nobody counted is the release defect
      // said one layer down — this is the string the group heading and
      // the list's counts-only body both render. An uncounted face names the
      // TYPE and declines the number, which is exactly what it knows.
      ? `${t.label}: not counted`
      : `${t.label}: ${group.capped ? 'at least ' : ''}${t.count}`)
    .join(' · ')
}

/**
 * The name a person sees for one far record, in EVERY lens.
 *
 * Never the uuid: a record the caller may not name is named by its TYPE and its
 * relationship, which is all they are entitled to know about it and all they
 * need in order to ask for access. It was shared with the per-group metro
 * lens deliberately — the two used to disagree, and metro's answer was an em
 * dash. The Graph workspace that replaced that lens inherits the same rule.
 */
function itemName(item: ConnectionItem): string {
  return item.title?.trim() || `A ${item.entity.replace(/_/g, ' ')} you cannot open`
}

/** A row that cannot open is not a button. A disabled one is skipped by the
    keyboard AND announced as a control — unreachable and mislabelled at once. */
function canOpen(item: ConnectionItem, onOpen?: (item: ConnectionItem) => void): boolean {
  return !!onOpen && item.openable !== false && item.nameable !== false
}

/**
 * Contains-match over every name a person can actually see on the row.
 *
 * `labelsOnly` is the host-rendered case: the rows on screen are the host's, so
 * the items this component holds are not the population being filtered, and
 * narrowing on them would hide a group that does contain the match.
 */
function filterGroups(
  groups: readonly ConnectionGroup[], query: string, labelsOnly = false,
): ConnectionGroup[] {
  if (!query.trim()) return [...groups]
  if (labelsOnly) return groups.filter((g) => matchesText(query, g.label))
  return groups
    .map((g) => ({
      ...g,
      // A group whose LABEL matches keeps all of its rows: "show me Employs" is
      // a sentence about the group, not about any one record in it.
      items: matchesText(query, g.label) ? g.items
        : g.items.filter((i) => matchesText(query, i.title, i.subtitle, ...(i.facts ?? []))),
    }))
    // #2131: `g.count > 0` compiles against the widened type and silently reads
    // UNKNOWN as "no rows, drop it" — so searching for a relationship nobody
    // counted would hide the very group being searched for, and the surface
    // would answer "no matches" about something that exists. Unknown stays.
    // A group the server counted as ZERO still drops: that is measured, and
    // "Employs 0" is not a search result.
    .filter((g) => g.items.length > 0
      || (matchesText(query, g.label) && (g.count === null || g.count > 0)))
}

/**
 * A relationship chip.
 *
 * `count` is `number | null` (#2131). A chip for a group nobody counted shows
 * its LABEL and no number — it is still a real relationship, still reachable,
 * still pinnable, and the one thing it cannot say is how many. Rendering `0`
 * there was the defect; rendering an em dash would be a glyph a screen reader
 * announces as punctuation, so the number is simply absent and the accessible
 * name says why.
 */
function Chip({ label, count, atLeast, active, pinned, onClick, testId }: {
  label: string; count: number | null; active: boolean; pinned?: boolean
  /** The number is a FLOOR: the counts door capped, or a group is uncounted. */
  atLeast?: boolean
  onClick: () => void; testId: string
}) {
  return (
    <button type="button" className={cx(s.chip, active && s.chipOn)} onClick={onClick}
      aria-pressed={active}
      data-testid={testId} data-pinned={pinned || undefined}
      data-count={count === null ? 'unknown' : 'known'}
      /* The visible chip is "Label 3" / "Label". Neither says what the number
         MEANS, and for an uncounted group there is no number to lean on at all,
         so the accessible name states the fact in words. */
      aria-label={count === null
        ? `${label}, count not available`
        : `${label}, ${atLeast ? 'at least ' : ''}${count}`}>
      <span className={s.chipLabel}>{label}</span>
      {count !== null && (
        <span className={cx(s.chipN, 'tnum')}>{atLeast ? `${count}+` : count}</span>
      )}
    </button>
  )
}

/**
 * ONE row for a relationship that is known to have nothing in it (#1992).
 *
 * What it replaces was two controls in two places saying overlapping things:
 * an `Add` in the section heading, and a sentence in the body with its own
 * inline `Create one`. They were not duplicates of each other — the heading
 * opened the governed lookup, the body jumped straight to a create form — so a
 * person had two buttons, no way to tell them apart, and no way to reach the
 * lookup and the form as the separate choices they are.
 *
 * The sentence and both actions now sit on one line inside the section
 * contour, so an empty relationship costs one row rather than a heading, a
 * paragraph and a floating button.
 *
 * It renders only where the host SAID the group is empty and knew it — see
 * `isGroupEmpty`. An unknown count never reaches here.
 */
function EmptyConnectionRow({ group, anchor, onAdd, onCreate }: {
  group: ConnectionGroup; anchor: string
  onAdd?: (group: ConnectionGroup) => void
  onCreate?: (group: ConnectionGroup) => void
}) {
  const headingId = `${anchor}-heading`
  const messageId = `${anchor}-empty-message`
  const noun = addNoun(group)
  return (
    <div className={s.emptyRow} data-testid={`${anchor}-empty-row`}>
      <span className={s.emptyText} id={messageId}>No {noun} yet.</span>
      {(onCreate || onAdd) && (
        <span className={s.emptyActions} role="group" aria-labelledby={`${headingId} ${messageId}`}>
          {onCreate && (
            <button type="button" className={s.emptyAct} onClick={() => onCreate(group)}
              data-testid={`${anchor}-create`}>Create new</button>
          )}
          {onAdd && (
            <button type="button" className={s.emptyAct} onClick={() => onAdd(group)}
              data-testid={`${anchor}-add`}>Add existing</button>
          )}
        </span>
      )}
    </div>
  )
}

/**
 * The noun a person is adding to, or missing from, one group.
 *
 * One far entity gives the noun the person is actually missing. A polymorphic
 * group has no single far noun, so its relationship label is the only truthful
 * fallback. Neither path ever exposes a schema key or record id.
 */
function addNoun(group: ConnectionGroup): string {
  return (group.byType.length === 1 ? group.byType[0]!.label : group.label).toLowerCase()
}

/**
 * THE PER-TYPE VIEW SELECTOR (#2349 §D2/§D3).
 *
 * A real `radiogroup` with a roving tabindex, not five buttons with
 * `role="radio"` — that combination is an ARIA pattern no automated check flags
 * and a keyboard user feels immediately: Tab walks the group one option at a
 * time while the arrow keys, the thing the role PROMISES, do nothing. Exactly
 * one option is tabbable, the arrows move and select together (the radiogroup
 * convention), and the wrap is deliberate so a five-item group has no dead end.
 * `UserMenu`'s Appearance strip is the same pattern for the same reason.
 *
 * ── it renders the OFFER, and only the offer ───────────────────────────────
 * `offered` arrives already narrowed twice — by the server's `available` for
 * this slot, and by what this host can actually draw (`bindingOf`). An
 * ineligible view is therefore not disabled here; it is not rendered, which is
 * the only form of "never show ineligible views" that cannot drift. A single
 * offered view draws no control at all: a radiogroup with one option is ornament
 * that still takes a tab stop.
 *
 * ── the icon is not the name ───────────────────────────────────────────────
 * Every option carries `LENS_LABEL` as its accessible name and its tooltip, and
 * the resolved view is ALSO written in words beside the type name. A person who
 * cannot tell which presentation is on cannot tell whether the control did
 * anything — which is what the named-lens span beside the count has been for
 * since it was added, and it stays.
 */
function LensSelector({ group, lens, offered, onChange, anchor, source, rung }: {
  group: ConnectionGroup
  lens: ConnectionsLens
  offered: readonly ConnectionsLens[]
  onChange: (lens: ConnectionsLens) => void
  anchor: string
  source?: 'default' | 'requested' | 'refused'
  rung?: string
}) {
  return (
    <span className={s.lensGroup}>
      <PresentationSwitcher<ConnectionsLens>
        active={lens}
        available={offered}
        onChange={onChange}
        label="Show"
        subject={group.label}
        source={source}
        rung={rung}
        testId={`${anchor}-lens`}
        optionDataAttribute="data-lens-option"
        presentation="icon"
        options={RELATED_PRESENTATIONS.map((value) => ({
          value,
          label: LENS_LABEL[value],
          icon: LENS_GLYPH[value],
        }))} />
    </span>
  )
}

function GroupSection({
  group, presentation, pinned, declared, onOpen, onAdd, onCreate, knownEmpty, testId,
  lens = 'grid', offered = ['grid', 'cards'], onLensChange, renderMap, renderTimeline, body, tools, addLabel,
  source, rung,
}: {
  group: ConnectionGroup; presentation: ConnectionsPresentation; pinned: boolean
  /** Placed by a form definition rather than by the count heuristic (#1466). */
  declared?: boolean
  onOpen?: (item: ConnectionItem) => void
  onAdd?: (group: ConnectionGroup) => void
  onCreate?: (group: ConnectionGroup) => void
  /** The host says this group is empty and that the emptiness is KNOWN. */
  knownEmpty?: boolean
  testId: string
  /** What THIS group reads as, already narrowed to what can be drawn. */
  lens?: ConnectionsLens
  /** What this group is allowed to read as, governed. */
  offered?: readonly ConnectionsLens[]
  onLensChange?: (lens: ConnectionsLens) => void
  /** How the active view was chosen, where the host knows (#2349 §D3). */
  source?: 'default' | 'requested' | 'refused'
  /** Which rung governs the default, for the explanation of an inherited one. */
  rung?: string
  renderMap?: (group: ConnectionGroup) => ReactNode
  renderTimeline?: (group: ConnectionGroup) => ReactNode
  /** The host's own rows for this group, when it has richer ones. */
  body?: ReactNode
  /** The host's controls for this group's header (the saved-view chip). */
  tools?: ReactNode
  /** The words on this group's Add, on the page. */
  addLabel?: string
}) {
  // The anchor id is what a counter chip scrolls to on the record page.
  // A section, not a sub-page: the form above it stays where it was.
  const anchor = sectionId(testId, group.key)
  const headingId = `${anchor}-heading`
  return (
    <section className={s.group} id={anchor} aria-labelledby={headingId}
      data-testid={anchor} data-pinned={pinned || undefined}
      data-lens={lens}
      data-declared={declared || undefined}>
      {/* ── THE TYPE HEADER: icon · name · count · current view · selector ──
          #2349 §D2, and the ORDER is the design's. What a person reads left to
          right is what this relationship IS, how many there are, and how it is
          being read; the control that changes the last of those is right-aligned
          at the end of the row, which is where §D3 puts it.

          Named ONCE. The mock's item 24 measured the same words three times on
          one group — a tab chip, this heading and the table's own header — and
          the chip strip above these sections is gone in the workspace
          presentation for that reason. */}
      <h3 className={s.groupHead} id={headingId}>
        {group.iconKey != null && (
          <IconTile size="sm" className={s.groupIcon} aria-hidden data-testid={`${anchor}-icon`}>
            <EntityIcon iconKey={group.iconKey} size={13} />
          </IconTile>
        )}
        <span className={s.groupLabel}>{group.label}</span>
        {/* #2131: no number for a group nobody counted. The heading keeps its
            label and its Add; what it loses is a `0` that claimed the server
            had looked. */}
        {group.count !== null && (
          <CountPill tone={presentation === 'workspace' ? 'brand' : 'neutral'} className={cx(s.groupN, 'tnum')}>
            {group.capped ? `${group.count}+` : group.count}
          </CountPill>
        )}
        {/* The host's view chip sits with the NAME it qualifies — "Addresses 1
            · All addresses ▾" reads as one phrase — and ahead of the actions. */}
        {tools}
        <span className={s.grow} />
        {/* One Add per section, never two (#1992). A KNOWN-empty group carries
            its actions in the row below, beside the sentence that explains why
            they are there; repeating the control in the heading gave one
            section two buttons that did different things — the heading's
            opened the governed lookup, the body's went straight to a form. */}
        {onAdd && !knownEmpty && (
          /* The shared Button. On the page it names what it adds ("Add
             address"), as the design's does; in the drawer "Add" under the
             group's own name already says it. */
          <Button size="sm" variant={presentation === 'workspace' ? 'secondary' : 'ghost'}
            className={s.add} onClick={() => onAdd(group)} data-testid={`${anchor}-add`}
            icon={presentation === 'workspace' ? <Plus size={13} aria-hidden /> : undefined}>
            {presentation === 'workspace' ? (addLabel ?? `Add ${addNoun(group)}`) : 'Add'}
          </Button>
        )}
        {/* The lens this section reads in, NAMED rather than left to the icon.
            A person who cannot see which presentation is on cannot tell whether
            the control did anything — and after #2349 the control IS icons, so
            this is the only place the current view is written in words. */}
        {/* …and it is named whenever there is a CONTROL **or** a REFUSAL.
            Gating this on the selector alone was a real hole, found by the spec
            that asserts it: a slot offering one view to a person who asked for
            another drew no control, so it also drew no name and no explanation —
            the exact case where the explanation matters most, because nothing on
            screen is even hinting that a choice was made. */}
        {(onLensChange && offered.length > 1) || source === 'refused' ? (
          <span className={s.groupLens} data-testid={`${anchor}-lens-name`}
            data-source={source ?? undefined}>
            {LENS_LABEL[lens]}
          </span>
        ) : null}
        {/* The sentence, where there is no selector to carry it. */}
        {source === 'refused' && !(onLensChange && offered.length > 1) && (
          <span className={s.srOnly} data-testid={`${anchor}-lens-source`}>
            {`Your choice is not available for ${group.label}; showing the governed ${LENS_LABEL[lens]} view.`}
          </span>
        )}
        {/* …and the control itself, in the header of the section it changes and
            at the RIGHT end of it (#2349 §D3). ONE offered lens needs no
            selector: a control with a single option is ornament that still takes
            a tab stop. */}
        {onLensChange && offered.length > 1 && (
          <LensSelector group={group} lens={lens} offered={offered}
            onChange={onLensChange} anchor={anchor} source={source} rung={rung} />
        )}
      </h3>

      {/* The body's POSITION and SHAPE never change with `knownEmpty`, and that
          is load-bearing rather than tidy. Wrapping it in a fragment on one
          branch and not the other made React unmount and remount the whole
          subtree the moment a group stopped being empty — which remounts the
          host's governed picker, whose open-signal effect then re-fires and
          raises the picker over whatever dialog the person is actually in. It
          cost an Escape that went to the wrong layer, and only the browser
          suite could see it.

          So the row is a SIBLING, rendered after. The body is not only rows:
          on the record page it mounts the picker this row's own Add opens. */}
      {/* The canvas, INSIDE the section it belongs to and above that section's
          own rows. #2276's clause, and the one a map is most likely to break:
          "Map may not erase relationships it cannot plot." A map-capable group
          is drawn twice — as pins and as its ordinary rows — because the rows
          are where its inline edits and its Add live, and none of those has a
          home on a canvas. A row with no coordinates stays named right here,
          in the same section, rather than vanishing into a legend. */}
      {lens === 'map' && renderMap?.(group)}

      {/* …and the timeline, on the same terms. The rows below stay for the
          same reason they stay under a map: a chart is a READING of the
          records, and the inline edits, the Add and the names all live on the
          rows. #2323's own clause — "no reading" is not zero — has nowhere to
          be said if the records it is about have been erased. */}
      {lens === 'timeline' && renderTimeline?.(group)}

      {body !== undefined ? body : countsOnly(group) ? (
        // A count with no NAMES is not a failure: `core.titles_of` is
        // caller-RLS, so a customer surface can legitimately know that three
        // things are linked and be allowed to name none of them. "People: 2" is
        // a better sentence than two rows that both say "a person you cannot
        // open" — the same fact, said once, in the shape it is actually in.
        <p className={s.muted}>{countFace(group)}</p>
      ) : group.items.map((item) => (
        <ConnectionRow key={item.key} item={item} presentation={presentation}
          onOpen={onOpen} testId={testId} />
      ))}

      {knownEmpty && (
        <EmptyConnectionRow group={group} anchor={anchor} onAdd={onAdd} onCreate={onCreate} />
      )}

      {/* #2131: `group.count !== null` is a real guard, not a type ceremony.
          `capped` says the counts door hit its cap — which it can only do
          having ANSWERED — so a capped group with an unknown total should be
          impossible. Should-be-impossible is exactly where a fabricated number
          gets printed, and "Showing 3 of at least null" is not a sentence. */}
      {!knownEmpty && body === undefined && group.capped && group.count !== null
        && group.items.length > 0 && (
        <p className={s.muted}>Showing {group.items.length} of at least {group.count}.</p>
      )}
    </section>
  )
}

function ConnectionRow({ item, presentation, onOpen, testId }: {
  item: ConnectionItem; presentation: ConnectionsPresentation
  onOpen?: (item: ConnectionItem) => void; testId: string
}) {
  const openable = canOpen(item, onOpen)
  const facts = [...(item.facts ?? []), item.ended ? 'ended' : null].filter(Boolean) as string[]
  const name = item.title?.trim()
  const body = (
    <>
      <span className={s.rowIcon}><EntityIcon iconKey={item.iconKey ?? 'record'} size={17} /></span>
      <span className={s.rowMain}>
        {/* `itemName` — never the uuid, and the same sentence every other lens
            over these records uses, because two lenses disagreeing about what a
            record is called is the defect this component exists to delete. */}
        <span className={s.rowName} data-anonymous={name ? undefined : true}>{itemName(item)}</span>
        {facts.length > 0 && <span className={s.rowFacts}>{facts.join(' · ')}</span>}
      </span>
    </>
  )
  const cls = cx(s.row, presentation === 'workspace' && s.rowWide, item.ended && s.rowEnded)
  return openable
    ? <button type="button" className={cls} onClick={() => onOpen?.(item)}
        data-testid={`${testId}-row`} data-ended={item.ended || undefined}>{body}</button>
    : <div className={cls} data-testid={`${testId}-row`} data-ended={item.ended || undefined}
        data-openable="false">{body}</div>
}

/* ── where the metro lens went (#2324) ─────────────────────────────────────
 *
 * This file used to end with a per-GROUP metro track: one line per relationship,
 * five stations and a terminus. The ruling that removed it is one sentence —
 * *"Metro does not describe how one relationship list is rendered; it describes
 * how the record's complete relationship network is laid out."*
 *
 * So it is not deleted, it is RESITED: Metro is a layout of the whole-record
 * Graph workspace, beside Network, over the same nodes and edges. The vocabulary
 * value survives with it (`core.view_kind` keeps the row and moves its surface
 * from `related` to `graph`), and this component keeps exactly what it always
 * had — the arrangement, the counts, the anchors and the chrome of a LIST.
 *
 * Two things the old lens got right are inherited by the graph rather than
 * rewritten, and are worth naming here because both were paid for:
 *
 *   · a record the caller may not name is drawn by the SAME sentence the list
 *     row uses and, because it cannot be opened, is not a button. A disabled
 *     control is skipped by the keyboard AND announced as a control;
 *   · a count with no names behind it is a count, never a track of anonymous
 *     dots and never a bare `+N` beside nothing.
 */

/* Re-declared beside the component whose props are written in it, so a host
   can type the data it hands this component from `@solum/ui` alone (#2406,
   design-sync kit pass: "hosts need @solum/experience to type their data").
   An alias, not a copy — the shape stays Experience's. */
export type ConnectionGroup = GovernedConnectionGroup
export type ConnectionItem = GovernedConnectionItem
